import {
    init as SentryInit,
    getCurrentScope as SentryGetCurrentScope,
    withScope as SentryWithScope,
    captureException as SentryCaptureException,
    captureMessage as SentryCaptureMessage,
    setAttributes as SentrySetAttributes,
    browserTracingIntegration as SentryBrowserTracing,
    browserApiErrorsIntegration as SentryBrowserApiErrors,
    thirdPartyErrorFilterIntegration as SentryThirdPartyErrorFilter,
    // replayIntegration as SentryReplay,
    // replayCanvasIntegration as SentryReplayCanvas,
    // showReportDialog as SentryShowReportDialog
} from '@sentry/browser';

// Must match SENTRY_APPLICATION_KEY injected by the Sentry bundler plugin in rsbuild.config.mjs
const SENTRY_APPLICATION_KEY = 'map-planner';

const TRACES_SAMPLE_RATE = 0.2;

export function initializeSentry() {
    const isTraced = Math.random() < TRACES_SAMPLE_RATE;

    SentryInit({
        dsn: window.SENTRY_JAVASCRIPT_KEY,

        integrations: [
            ...(isTraced ? [SentryBrowserTracing({
                shouldCreateSpanForRequest: (url) => !url.includes('pmaps.map-planner.com')
            })] : []),
            // Do not wrap every animation frame: MapLibre renders through requestAnimationFrame, so each map
            // frame paid the wrapper and traces attributed the map's rendering to the Sentry bundle.
            SentryBrowserApiErrors({requestAnimationFrame: false}),
            // Tag only for now (nothing dropped): errors with no frame from our bundles get third_party_code:true
            SentryThirdPartyErrorFilter({
                filterKeys: [SENTRY_APPLICATION_KEY],
                behaviour: 'apply-tag-if-exclusively-contains-third-party-frames'
            }),
            // SentryReplay({
            //     maskAllText: false,
            //     maskAllInputs: false,
            //     blockAllMedia: false
            // }),
            // // // For recording canvas elements
            // SentryReplayCanvas()
        ],

        // IP addresses are inferred by default since v11 (dataCollection.userInfo), as sendDefaultPii did.

        // v11 attaches a synthetic stack to captureMessage by default: pushError's HTTP messages would then be
        // grouped by call site instead of by message ("Not Found, 404" and "…, 500" merged in one issue).
        attachStacktrace: false,

        // Sampled above (TRACES_SAMPLE_RATE): a traced load keeps all its transactions, the others have none.
        tracesSampleRate: isTraced ? 1.0 : 0,
        tracePropagationTargets: [
            'localhost',
            /^https:\/\/www\.map-planner\.com\/api/,
            /^https:\/\/www\.map-planner\.fr\/api/,
            /^https:\/\/www\.map-planner\.de\/api/,
            /^https:\/\/www\.map-planner\.it\/api/,
            /^https:\/\/www\.map-planner\.es\/api/
        ],

        // // Replays will be captured for 0% of all normal sessions
        // replaysSessionSampleRate: 0.1,
        // // Replays will be captured 100% of all sessions with an error
        // replaysOnErrorSampleRate: 1.0,

        ignoreErrors: [
            'TypeError: Failed to fetch',
            'TypeError: NetworkError when attempting to fetch resource.',
            'TypeError: cancelled',
            'TypeError: annulé',
            'TypeError: annullato',
            'TypeError: Load failed',
            'TypeError: AbortError: Fetch is aborted',
            'AbortError: Fetch is aborted',
            'UnknownError: Background Sync is disabled.',
            'Error: QuotaExceededError: QuotaExceededError',
            /The operation was aborted/,
            /Maximum call stack size exceeded/,
            /did not respond to this postMessage/,
            /Java object is gone/,
            /Java bridge method invocation error/,
            /does not implement interface Node/,
            /Can't find variable: _G/,
            /basket\.products/,
            /feature named .* was not found/,
            /Div Map container not found/,
            /ChunkLoadError/,
            /Loading CSS chunk/,
            /Unable to preload CSS/,
            /webkitExitFullScreen/,
            /webkitEnterFullscreen/,
            /fullscreen error/,
            /cancelled by exitFullscreen/,
            /setIOSParameters/,
            /_reportEvent/,
            /@webkit/,
            /no-response/,
            /feature index out of bounds/,
            /Out of bounds\. Index requested/,
            /clearHighlight/,
            /getImageData/,
            /media resource was aborted/,
            /Refused to evaluate a string as JavaScript/,
            /error loading dynamically imported module/,
            /Importing a module script failed/,
            /Unexpected identifier/,
            /Unexpected token/,
            /runtime\.sendMessage/,
            /Can't find variable: gmo/,
            /Could not compile/,
            /An error occured reading the Blob argument to createImageBitmap/,
            /Framebuffer is not complete/,
            /The play method is not allowed/,
            /Program failed to link/,
            /invalid origin/,
            /shaderPreludeCode/,
            /effect_update_depth_exceeded/,
            /state_unsafe_mutation/,
            /Exceeded storage quota/,
            /this\.terrain\.sourceCache/,
            /AJAXError: /,
            /InvalidStateError/,
            /Invalid regular expression/,
            /CustomEvent/,
            /InvalidCharacterError/,
            /OffscreenCanvas/,
            /signal is aborted without reason/,
            /Share canceled/,
            /Abort due to cancellation of share/,
            /getLayer/,
            /imageManager/,
            /tileManager/,
            /updateTransitions/,
            /ResizeObserver loop limit exceeded/,
            /_layers/,
            /not granted/,
            /Object Not Found Matching Id/,
            /extDomain is not defined/
        ],
        denyUrls: [
            /pagead\/js/i,
            /graph\.facebook\.com/i,
            /connect\.facebook\.net\/en_US\/all\.js/i,
            /extensions\//i,
            /^chrome:\/\//i,
            /^chrome-extension:\/\//i,
            /^safari-extension:\/\//i,
            /^webkit-masked-url:\/\//i,
            /s\.pinimg\.com/i,
            /translate\.googleapis\.com/i,
            /metrics\.l-x\.fr/i,
            /atob/i,
            /pmaps\.map-planner\.com/i,
            /^file:\/\//i
        ],
        beforeSend(event) {
            if (window.location.protocol === 'file:') {
                return null;
            }

            return event;
        }
    });

    const scope = SentryGetCurrentScope();
    scope.setLevel('warning');

    if (window.currentUserId || window.currentUserSlug) {
        scope.setUser({
            id: window.currentUserId,
            username: window.currentUserSlug
        });
    }

    scope.setTag('locale', window.locale);

    scope.setTag('role', window.currentUserRole || 'visitor');

    // Since v11, scope tags no longer reach spans: attributes carry them to performance data.
    SentrySetAttributes({
        locale: window.locale,
        role: window.currentUserRole || 'visitor'
    });
}

window.SentryWithScope = SentryWithScope;
window.SentryCaptureException = SentryCaptureException;
window.SentryCaptureMessage = SentryCaptureMessage;

export {SentryWithScope, SentryCaptureException, SentryCaptureMessage};
